<template>
    <app-layout v-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header :heading="pageTitle"></common-page-header>
            </common-content-spacer>

            <common-page-plain>
                <common-content-spacer height="medium">
                    <div v-if="locationsWithoutAvailability.length && $route.params.action === 'schedule'">
                        <common-content-spacer height="small">
                            <common-page-header size="small" :heading="getUnsetLocationsHeading"></common-page-header>
                            <div>
                                <p v-for="(location, index) in locationsWithoutAvailability" :key="index">
                                    <span
                                        v-html="
                                            $t('pages.availability.index.availability.set_availability', {
                                                locationName: location.label,
                                            })
                                        "
                                    ></span>
                                    <span @click="changeRouteAction(location.value)"><u>here</u></span>
                                </p>
                            </div>
                        </common-content-spacer>
                    </div>

                    <schedule-form
                        v-if="showModifyAvailability && locations.length"
                        :locations="locations"
                        :dayFrom="7"
                        :dayTo="19"
                        @modify-availability="changeRouteAction"
                        @availability-changed="loadScheduler"
                    ></schedule-form>
                    <availability-schedule
                        v-else
                        :schedule="schedule"
                        :user="user"
                        :practice="practice"
                        :practiceLocationId="practiceLocationId"
                        @modify-availability="changeRouteAction"
                    ></availability-schedule>
                </common-content-spacer>
            </common-page-plain>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import UserAPI from 'api/UsersAPI'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ScheduleForm from 'components/forms/AvailabilityScheduleForm'
    import AvailabilitySchedule from 'components/page-elements/AvailabilitySchedule'
    import AppLayout from 'components/layouts/AppLayout'
    import Store from '~/store'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin],

        components: {
            AppLayout,
            ScheduleForm,
            AvailabilitySchedule,
        },

        data() {
            return {
                user: this.$auth.getActiveMember(),
                practice: this.$auth.getPractice(),
                schedule: {},
                practiceLocationId: undefined,
                locationsWithoutAvailability: [],
                locations: [],
            }
        },

        computed: {
            pageTitle() {
                return this.showModifyAvailability
                    ? this.$t('pages.availability.index.titles.modify_availability')
                    : this.$t('pages.availability.index.titles.main_location_schedule', { name: this.getLocationName() })
            },

            getUnsetLocationsHeading() {
                return this.$t('pages.availability.index.availability.number_of_unset_locations', {
                    numberOfLocations: this.locationsWithoutAvailability.length,
                    multiple: this.locationsWithoutAvailability.length > 1 ? 's' : '',
                })
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.availability.index.breadcrumbs.availibility'),
                        route: this.$route.query.locationId
                            ? {
                                  name: 'availability',
                                  params: { action: 'schedule' },
                                  query: { locationId: this.$route.query.locationId },
                              }
                            : { name: 'availability', params: { action: 'schedule' } },
                    },
                    {
                        label: this.getLocationName(),
                        route: this.$route.query.locationId
                            ? {
                                  name: 'availability',
                                  params: { action: 'schedule' },
                                  query: { locationId: this.$route.query.locationId },
                              }
                            : { name: 'availability', params: { action: 'schedule' } },
                    },
                    {
                        label: this.$t('pages.availability.index.breadcrumbs.schedule'),
                    },
                ]
            },

            showModifyAvailability() {
                return this.$route.params.action === 'modify'
            },
        },

        methods: {
            getLocations() {
                const practice = this.$auth.getPractice()
                this.practice = practice
                this.locations = [
                    { label: 'Main', value: '0', isAvailabilitySet: practice.isAvailabilitySet },
                    ...practice.locations.map((location) => ({
                        label: location.name,
                        value: location.id,
                        isAvailabilitySet: location.isAvailabilitySet,
                    })),
                ]

                this.checkLocationsWithNoAvailability()
            },

            getLocationName() {
                return this.$route.query.locationId
                    ? this.$auth
                          .getPractice()
                          .locations.filter((loc) => loc.id === Number(this.$route.query.locationId))
                          .map((loc) => loc.name)[0]
                    : this.$t('pages.availability.index.breadcrumbs.main_location')
            },

            changeRouteAction(locationId) {
                let practiceLocationId
                if (locationId || locationId === '0') {
                    practiceLocationId = locationId
                } else {
                    practiceLocationId = this.$route.query.locationId
                }
                const action = this.showModifyAvailability ? 'schedule' : 'modify'

                let route = {
                    name: 'availability',
                    params: { action },
                }
                if (practiceLocationId) {
                    route.query = { locationId: practiceLocationId }
                }

                this.$router.push(route)
            },

            loadScheduler() {
                if (this.$route.query.locationId) {
                    this.practiceLocationId = Number(this.$route.query.locationId)
                    UserAPI.getAvailability(this.user.id, this.practice.id, this.practiceLocationId)
                        .then((res) => (this.schedule = Object.assign(this.schedule, res)))
                        .catch(() => Store.dispatch('ui/showNotFoundPage'))
                } else {
                    this.practiceLocationId = undefined
                    UserAPI.getAvailability(this.user.id, this.practice.id)
                        .then((res) => (this.schedule = Object.assign(this.schedule, res)))
                        .catch(() => Store.dispatch('ui/showNotFoundPage'))
                }
            },

            checkLocationsWithNoAvailability() {
                this.locationsWithoutAvailability = this.locations.filter((location) => !location.isAvailabilitySet)
            },

            openAvailabilityForLocation(location) {
                const practiceLocationId = location.value
                const action = this.showModifyAvailability ? 'schedule' : 'modify'

                let route = {
                    name: 'availability',
                    params: { action },
                }
                if (practiceLocationId) {
                    route.query = { locationId: practiceLocationId }
                }

                this.$router.push(route)
            },
        },

        watch: {
            $route: {
                immediate: true,
                handler() {
                    this.loadScheduler()
                    this.getLocations()
                },
            },
        },
    }
</script>

<style>
    .mx-datepicker.mx-datepicker-inline,
    .mx-calendar.mx-calendar-panel-date {
        width: 100%;
    }
    .mx-calendar-content .cell.active {
        border-radius: 10px;
        background-color: #13b3e3;
    }
    .mx-calendar-content .cell:hover {
        border-radius: 10px;
    }
    .mx-calendar-content .cell.disabled:hover {
        border-radius: 0;
    }
</style>
